/**
 * AiAgentHttpService — REST client for aumico-ms-ai-agents.
 *
 * Endpoints:
 *   POST /api/ai/jobs          → submit a new AI job
 *   GET  /api/ai/sessions/:id  → poll session status + result
 *   POST /api/ai/sessions/:id/chat → send a follow-up message
 *   GET  /api/ai/abcp/runs               → list a closing's runs (REV-21869 #165)
 *   GET  /api/ai/abcp/runs/:runId/trail  → one run's trail (REV-21869 #165)
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AUMICO_AI_AGENTS_CONFIG, AumicoAiAgentsConfig } from '../aumico-ai-agents.config';
import { AiAgentJobRequest } from '../interfaces/ai-agent-job-request.interface';
import { AiAgentSession, AiJobResponse, AiChatResponse } from '../interfaces/ai-agent-session.interface';
import { AiAbcpRunSummary, AiAbcpTrailPage } from '../interfaces/ai-abcp-run-trail.interface';

@Injectable()
export class AiAgentHttpService {
    private readonly baseUrl: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(AUMICO_AI_AGENTS_CONFIG) private readonly config: AumicoAiAgentsConfig,
    ) {
        // Ensure baseUrl always ends with '/' so template literals like `${baseUrl}ai/jobs` work
        this.baseUrl = config.apiGateway.endsWith('/') ? config.apiGateway : `${config.apiGateway}/`;
    }

    /**
     * POST /api/ai/jobs → { sessionId }
     * Submits a new AI agent job.
     */
    submitJob$(request: AiAgentJobRequest): Observable<AiJobResponse> {
        return this.http.post<AiJobResponse>(`${this.baseUrl}ai/jobs`, request);
    }

    /**
     * GET /api/ai/sessions/:id → full session with status, output, trajectory, etc.
     */
    getSession$(sessionId: string): Observable<AiAgentSession> {
        return this.http.get<AiAgentSession>(`${this.baseUrl}ai/sessions/${sessionId}`);
    }

    /**
     * POST /api/ai/sessions/:id/chat → { sessionId, reply }
     * Sends a follow-up chat message to the agent.
     * @param sectionKey Optional — restricts processing to the specified section only.
     */
    sendChat$(sessionId: string, message: string, sectionKey?: string): Observable<AiChatResponse> {
        return this.http.post<AiChatResponse>(
            `${this.baseUrl}ai/sessions/${sessionId}/chat`,
            { message, ...(sectionKey ? { sectionKey } : {}) },
        );
    }

    /**
     * GET /api/ai/abcp/runs?mandateId=…&closingId=… → the closing's runs, newest `startedAt` first
     * (`abcp-runs.controller.ts:47-56`).
     *
     * ⚠️ BOTH ids are mandatory (REV-21869 #165, Runde 3 / SRR F1): `ListRunsQueryDto` carries
     * `mandateId` **and** `closingId`, both `@MinLength(1)`, behind `whitelist: true,
     * forbidNonWhitelisted: true` — a single param, or a param this DTO does not know, is a 400 on
     * the real service. `mandateId` is the company's id (`report.companyId`), `closingId` is the
     * annual report's own `_id` — NEITHER is the encrypted route id (`abcp-demo-panel.page.ts:250-257`).
     */
    listRuns$(mandateId: string, closingId: string, accountId?: string): Observable<AiAbcpRunSummary[]> {
        // `accountId` narrows to the runs that covered this account — the service lists the newest 50 runs,
        // and since a run is per trigger, one account's history disappears behind other accounts' runs
        // without it (review 20.10.2026, Korrektur 1).
        let params = new HttpParams().set('mandateId', mandateId).set('closingId', closingId);
        if (accountId !== undefined) {
            params = params.set('accountId', accountId);
        }
        return this.http.get<AiAbcpRunSummary[]>(`${this.baseUrl}ai/abcp/runs`, { params });
    }

    /**
     * GET /api/ai/abcp/runs/:runId/trail → one run's trail page.
     *
     * ⚠️ `mandateId`/`closingId` are mandatory here too — `TrailQueryDto extends ListRunsQueryDto`
     * (REV-21869 #165, Runde 3 / SRR F1). `sinceSequence`/`limit` are the pagination cursor and page
     * size (`abcp-run-query.dto.ts:5-6,51-63`).
     *
     * ⚠️ NO `accountId` is sent (REV-21869 #165, Runde 2 / A-C3): the server's own `?accountId=`
     * filter throws away the run-level warnings that carry no account, and the pane's row rule
     * needs those. The caller filters client-side with `selectAccountTrailEntries`.
     */
    getTrail$(
        runId: string,
        mandateId: string,
        closingId: string,
        sinceSequence?: number,
        limit?: number,
    ): Observable<AiAbcpTrailPage> {
        let params = new HttpParams().set('mandateId', mandateId).set('closingId', closingId);
        if (sinceSequence !== undefined) {
            params = params.set('sinceSequence', String(sinceSequence));
        }
        if (limit !== undefined) {
            params = params.set('limit', String(limit));
        }
        return this.http.get<AiAbcpTrailPage>(`${this.baseUrl}ai/abcp/runs/${runId}/trail`, { params });
    }
}
